import type { Result } from "../../../../lib/patterns/result";
import { Err, Ok, isResultErr, isResultOk } from "../../../../lib/patterns/result";
import type { FILE_PARSING_ERROR } from "../../common/file";
import { parseFile } from "../../common/file";
import type { Parser, ParsingError, ParsingErrorMessageMap, ParsingResult } from "../../types";
import {
  columns,
  columnsMandatory,
  // hasDuplicatedRowByQuantityId,
  hasFiltersNotFloat,
  hasFormulaQuantitiesWithMaxStd,
  hasFormulaSkippedQuantity,
  hasInvalidColumns,
  hasInvalidDescriptionSize,
  hasInvalidFormatEquipments,
  hasInvalidHiddenField,
  hasInvalidFormulaSize,
  hasInvalidLockedField,
  hasInvalidPairValueFormulaSensor,
  hasInvalidQuantityNameFormat,
  hasInvalidQuantityNameSize,
  hasInvalidQuantitySensorTagSize,
  hasInvalidStartDateFormat,
  hasInvalidSensorTagInSource,
  hasInvalidUnitSize,
  hasLockedTrueWithoutFormula,
  hasMinSuperiorToMax,
  hasMissingSensorTagORformula,
  hasNegativeMaxStd,
  normalizeStartDateToUTC,
} from "./rules";
import parseCSV from "../../../../lib/csv";

/**
 * @description Data that can be parsed from an quantity file.
 */

export type QuantityParsingResultData = {
  quantities: Quantity[]; //Input<"POST", "/projects/:projectId(uuid)/quantities/import">["body"]
  formulaVersions: FormulaVersion[];
  ignoredQuantities: { quantity: string; sensorTag: string }[];
  ignoredFormulas: { quantity: string }[];
};
export type QuantityParsingResult = ParsingResult<QuantityParsingResultData>;
export type QuantityParsingError = ParsingError<QUANTITY_PARSING_ERROR | FILE_PARSING_ERROR>;

/**
 * @description Error that can be thrown by the quantity parser.
 */
export enum QUANTITY_PARSING_ERROR {
  MISSING_COLUMNS = "MISSING_COLUMNS" /* "Some columns are missing for lithium version" */,
  DATA_INVALID_PAIR_VALUE_FORMULA_SENSOR = "DATA_INVALID_PAIR_VALUE_FORMULA_SENSOR" /* "Formula and sensor tag cannot be defined at the same time" */,
  // DATA_QUANTITY_DUPLICATED = "DATA_QUANTITY_DUPLICATED" /* "Quantity name already exists" */,
  DATA_MIN_MAX_STD_INVALID = "DATA_MIN_MAX_STD_INVALID" /* "Minimum value should be inferior to maximum value" */,
  DATA_MIN_SUP_MAX_INVALID = "DATA_MIN_SUP_MAX_INVALID" /* "Maximum  value should be superior to minimum value" */,
  DATA_NEGATIVE_MAXSTD_INVALID = "DATA_NEGATIVE_MAXSTD_INVALID" /* "Maximum standard deviation should be positive" */,
  DATA_MAXSTD_WITH_FORMULA_INVALID = "DATA_MAXSTD_WITH_FORMULA_INVALID" /* "Formula quantities should not have a maximum standard deviation" */,
  DATA_QUANTITY_NAME_NOT_ALLOWED = "DATA_QUANTITY_NAME_NOT_ALLOWED" /* "Quantity name not allowed because not present in project" */,
  DATA_FILE_EMPTY = "DATA_FILE_EMPTY" /* "File is empty" */,
  DATA_FILE_QUANTITY_NAME_TOO_LONG = "DATA_FILE_QUANTITY_NAME_TOO_LONG" /* "Quantity name is too long" */,
  DATA_FILE_DESCRIPTION_TOO_LONG = "DATA_FILE_DESCRIPTION_TOO_LONG" /* "Description is too long" */,
  DATA_FILE_UNIT_TOO_LONG = "DATA_FILE_UNIT_TOO_LONG" /* "Unit is too long" */,
  DATA_FILE_SENSOR_TAG_TOO_LONG = "DATA_FILE_SENSOR_TAG_TOO_LONG" /* "Sensor tag is too long" */,
  DATA_FILE_FORMULA_TOO_LONG = "DATA_FILE_FORMULA_TOO_LONG" /* "Formula is too long" */,
  DATA_INVALID_QUANTITY_NAME_FORMAT = "DATA_INVALID_QUANTITY_NAME_FORMAT" /* "Quantity name not allowed" */,
  DATA_INVALID_EQUIPMENTS = "DATA_INVALID_EQUIPLENTS" /* "Equipments not allowed" */,
  DATA_INVALID_PERMISSION_SYSTEM_CREATION = "DATA_INVALID_PERMISSION_SYSTEM_CREATION" /* "Permission for system creation" */,
  DATA_INVALID_PERMISSION_EQUIPMENT_TYPE_CREATION = "DATA_INVALID_PERMISSION_EQUIPMENT_TYPE_CREATION" /* "Permission for equipment type creation" */,
  DATA_INVALID_PERMISSION_EQUIPMENTS = "DATA_INVALID_PERMISSION_EQUIPMENTS" /* "Permission for equipments creation" */,
  DATA_INVALID_MISSING_SENSOR_TAG_OR_FORMULA = "DATA_INVALID_MISSING_SENSOR_TAG_OR_FORMULA" /* "Sensor tag or formula is missing" */,
  DATA_INVALID_LOCKED_FIELD = "DATA_INVALID_LOCKED_FIELD" /* "Locked field must contain booleans (true or false) or be empty" */,
  DATA_INVALID_HIDDEN_FIELD = "DATA_INVALID_HIDDEN_FIELD" /* "Hidden field must contain booleans (true or false) or be empty" */,
  DATA_LOCKED_TRUE_WITHOUT_FORMULA = "DATA_LOCKED_TRUE_WITHOUT_FORMULA" /* "Locked can't be true for a non-formula quantity" */,
  DATA_FORMULA_VERSION_DUPLICATE_STARTS_AT = "DATA_FORMULA_VERSION_DUPLICATE_STARTS_AT" /* "Duplicate startsAt for a formula version" */,
  DATA_FORMULA_VERSION_EMPTY_FORMULA = "DATA_FORMULA_VERSION_EMPTY_FORMULA" /* "Formula version without formula" */,
  DATA_START_DATE_MISSING_BASE = "DATA_START_DATE_MISSING_BASE" /* "Start date requires a base row" */,
  DATA_FORMULA_VERSION_BASE_FORMULA_REQUIRED = "DATA_FORMULA_VERSION_BASE_FORMULA_REQUIRED" /* "Versioned quantity requires a base formula" */,
  DATA_FORMULA_VERSION_MULTIPLE_BASE_ROWS = "DATA_FORMULA_VERSION_MULTIPLE_BASE_ROWS" /* "Versioned quantity cannot define multiple base rows" */,
  DATA_FORMULA_VERSION_BASE_SENSOR_TAG_NOT_ALLOWED = "DATA_FORMULA_VERSION_BASE_SENSOR_TAG_NOT_ALLOWED" /* "Versioned quantity base row cannot define a sensorTag" */,
  DATA_START_DATE_INVALID_FORMAT = "DATA_START_DATE_INVALID_FORMAT" /* "Start date must be ISO formatted" */,
  DATA_FORMULA_VERSION_UNIT_MISMATCH = "DATA_FORMULA_VERSION_UNIT_MISMATCH" /* "Formula versions must share the same unit" */,
  DATA_FORMULA_VERSION_SENSOR_TAG_MISMATCH = "DATA_FORMULA_VERSION_SENSOR_TAG_MISMATCH" /* "Formula versions must share the same sensorTag" */,
  DATA_FORMULA_VERSION_METADATA_MISMATCH = "DATA_FORMULA_VERSION_METADATA_MISMATCH" /* "Formula version rows can only override formula and startDate" */,
}

export type QuantityParsingErrorMessageMap = ParsingErrorMessageMap<
  QUANTITY_PARSING_ERROR | FILE_PARSING_ERROR
>;

/**
 * @description Parse input model file content.
 * @param file Input model file to parse
 * @returns
 */
export const parseQuantity: Parser<
  QuantityParsingResult,
  QuantityParsingError[],
  {
    sensorTags: string[];
  }
> = async (file, { sensorTags }) => {
  /**
   * @description Check for common file issue.
   */
  const errors: QuantityParsingError[] = [];
  const fileResult = await parseFile(file, {
    expectedMimeType: ["text/csv", "application/vnd.ms-excel"],
  });

  /** Have to stop here, to handle file error */
  if (isResultErr(fileResult)) {
    errors.push(fileResult.value);
    return Err(errors);
  }

  /**
   * @description Backwards compatibility.
   */
  const csv = await file.text();
  const csvParsed = parseCSV(csv, "line");
  const data = csvParsed?.map((e, index) => ({
    ...e,
    line: String(index + 1),
  })) as
    | {
        [key: string]: string;
      }[]
    | undefined;

  /**
   * Get the version.
   */

  if (!data) {
    errors.push({
      kind: QUANTITY_PARSING_ERROR.MISSING_COLUMNS,
      explain: "file missing all columns",
      location: { file: file.name, line: 0 },
      data: {},
    });
    return Err(errors);
  }

  /**
   * Get file content.
   */
  const content = fileResult.value as string;

  /**
   * Formatted file content.
   */
  const [header] = content.split("\n").map(line =>
    line
      .replace(/\r/, "")
      .split(";")
      .map(v => v.replaceAll('"', "")),
  );

  /**
   * We focus on a single column naming without taking into account the different versions between Lithium and Helium.
   */
  const columnHeaderNormalizes = columns.flatMap(([col]) => col ?? []);

  /**
   * Check Format
   */
  if (hasInvalidColumns(header)) {
    errors.push({
      kind: QUANTITY_PARSING_ERROR.MISSING_COLUMNS,
      explain: "file don't contains expected columns",
      location: { file: file.name, line: 0 },
      data: {
        hint: columnsMandatory.join(", "),
        value: header?.join(", "),
      },
    });
  }

  const targetData = data.map(line =>
    columnHeaderNormalizes.reduce<{ [key: string]: string | undefined }>((acc, columnName) => {
      if (columnName in acc && acc[columnName] !== undefined && acc[columnName] !== null) {
        return acc;
      }

      return {
        ...acc,
        [columnName]: line[columnName],
      };
    }, {}),
  );

  /**
   * @description Check by line data.
   * @see {parseData}
   */
  const dataResult = parseQuantityData(file, targetData, sensorTags);
  if (isResultErr(dataResult)) errors.push(...dataResult.value);

  // const quantityIds = targetData.map(({ name: quantityId } = {}) => quantityId).filter(Boolean);
  // if (hasDuplicatedRowByQuantityId(quantityIds))
  //   errors.push({
  //     kind: QUANTITY_PARSING_ERROR.DATA_QUANTITY_DUPLICATED,
  //     explain: "file contain duplicated row",
  //     location: { file: file.name, line: 0 },
  //     data: {
  //       key: "name",
  //       value: quantityIds.find((quantityId, index) => quantityIds.indexOf(quantityId) !== index),
  //     },
  //   });

  if (isResultOk(dataResult)) {
    if (dataResult.value.quantities.length === 0) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_FILE_EMPTY,
        explain: "file is empty",
        location: { file: file.name, line: 0 },
        data: {},
      });
    }
  }

  /** if any errors, return them, otherwise return parser result */
  return errors.length > 0
    ? Err(errors)
    : Ok({
        data: {
          quantities: (dataResult.value as unknown as QuantityParsingResultData).quantities,
          formulaVersions: (dataResult.value as unknown as QuantityParsingResultData)
            .formulaVersions,
          ignoredQuantities: (dataResult.value as unknown as QuantityParsingResultData)
            .ignoredQuantities,
          ignoredFormulas: (dataResult.value as unknown as QuantityParsingResultData)
            .ignoredFormulas,
        },
      });
};

export interface Quantity {
  name: string;
  unit: string | null;
  description: string | null;
  formula: string;
  sensorTag: string;
  categories: {
    systemId: string | null;
    equipmentTypeId: string | null;
    equipmentNames: string[];
  };
  locked: boolean | null;
  hidden: boolean | null;
  filter: {
    min: number | null;
    max: number | null;
    maxStd: number | null;
  };
}

export interface FormulaVersion {
  name: string;
  formula: string;
  startsAt: Date;
}

type QuantityCsvRow = {
  line: number;
  name: string;
  unit: string;
  description: string;
  sensorTag: string;
  formula: string;
  systemId: string;
  equipmentTypeId: string;
  equipments: string;
  min: string;
  max: string;
  maxStd: string;
  locked: string;
  hidden: string;
  startsAt: string;
};

const normalizeOptionalText = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeOptionalNumber = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  const parsed = parseFloat(trimmed);
  return Number.isNaN(parsed) ? trimmed : parsed;
};

const normalizeOptionalBoolean = (value: string) => {
  const trimmed = value.trim().toLowerCase();
  if (trimmed.length === 0) return null;
  return trimmed === "true" ? true : trimmed === "false" ? false : null;
};

const normalizeOptionalEquipmentNames = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return trimmed
    .split(",")
    .map(item => item.trim())
    .filter(item => item.length > 0)
    .sort((a, b) => a.localeCompare(b));
};

const hasSparseVersionMetadataMismatch = (baseRow: QuantityCsvRow, versionRow: QuantityCsvRow) => {
  const comparableValues = [
    [normalizeOptionalText(versionRow.unit), normalizeOptionalText(baseRow.unit)],
    [normalizeOptionalText(versionRow.description), normalizeOptionalText(baseRow.description)],
    [normalizeOptionalText(versionRow.sensorTag), normalizeOptionalText(baseRow.sensorTag)],
    [normalizeOptionalText(versionRow.systemId), normalizeOptionalText(baseRow.systemId)],
    [
      normalizeOptionalText(versionRow.equipmentTypeId),
      normalizeOptionalText(baseRow.equipmentTypeId),
    ],
    [
      normalizeOptionalEquipmentNames(versionRow.equipments),
      normalizeOptionalEquipmentNames(baseRow.equipments),
    ],
    [normalizeOptionalNumber(versionRow.min), normalizeOptionalNumber(baseRow.min)],
    [normalizeOptionalNumber(versionRow.max), normalizeOptionalNumber(baseRow.max)],
    [normalizeOptionalNumber(versionRow.maxStd), normalizeOptionalNumber(baseRow.maxStd)],
    [normalizeOptionalBoolean(versionRow.locked), normalizeOptionalBoolean(baseRow.locked)],
    [normalizeOptionalBoolean(versionRow.hidden), normalizeOptionalBoolean(baseRow.hidden)],
  ] as const;

  return comparableValues.some(([versionValue, baseValue]) => {
    if (versionValue === null) return false;
    return JSON.stringify(versionValue) !== JSON.stringify(baseValue);
  });
};

const parseQuantityData = (
  file: File,
  data: {
    [key: string]: string | undefined;
  }[],
  sensorTags: string[],
): Result<QuantityParsingResultData, QuantityParsingError[]> => {
  const errors: QuantityParsingError[] = [];
  const quantities: Quantity[] = [];
  const formulaVersions: FormulaVersion[] = [];

  const rows: QuantityCsvRow[] = data.map((row, line) => ({
    line,
    name: row["name"] ?? "",
    unit: row["unit"] ?? "",
    description: row["description"] ?? "",
    sensorTag: row["sensorTag"] ?? "",
    formula: row["formula"] ?? "",
    systemId: row["system"] ?? "",
    equipmentTypeId: row["equipmentType"] ?? "",
    equipments: row["equipments"] ?? "",
    min: row["minimumValue"] ?? "",
    max: row["maximumValue"] ?? "",
    maxStd: row["maximumStd"] ?? "",
    locked: row["locked"] ?? "",
    hidden: row["hidden"] ?? row["intermediateCalculation"] ?? "",
    startsAt: row["startDate"] ?? row["startsAt"] ?? "",
  }));

  const rowsByName = rows.reduce((acc, row) => {
    const existing = acc.get(row.name) ?? [];
    existing.push(row);
    acc.set(row.name, existing);
    return acc;
  }, new Map<string, QuantityCsvRow[]>());

  const ignoredQuantities: { quantity: string; sensorTag: string }[] = rows
    .filter(row => hasInvalidSensorTagInSource(sensorTags, row.sensorTag))
    .map(row => ({ quantity: row.name, sensorTag: row.sensorTag }));

  const ignoredFormulas: { quantity: string }[] = [];
  for (const row of rows) {
    if (
      hasFormulaSkippedQuantity(
        row.formula,
        row.sensorTag,
        ignoredQuantities.map(({ quantity }) => quantity),
        ignoredFormulas.map(({ quantity }) => quantity),
      )
    )
      ignoredFormulas.push({ quantity: row.name });
  }

  for (const row of rows) {
    const {
      line,
      name,
      unit,
      description,
      sensorTag,
      formula,
      equipments,
      min,
      max,
      maxStd,
      locked,
      hidden,
      startsAt,
    } = row;

    if (hasInvalidQuantityNameSize(name)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_FILE_QUANTITY_NAME_TOO_LONG,
        explain: "quantity 'name' is too long",
        location: { file: file.name, line },
        data: {
          hint: name,
          value: name,
        },
      });
    }

    if (hasInvalidUnitSize(unit)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_FILE_UNIT_TOO_LONG,
        explain: "'unit' is too long",
        location: { file: file.name, line },
        data: {
          key: "unit",
          hint: unit,
          value: name,
        },
      });
    }

    const hasVersionedRowsForName = (rowsByName.get(name) ?? []).some(
      candidate => candidate.startsAt.trim() !== "",
    );
    if (!startsAt && hasMissingSensorTagORformula(sensorTag, formula) && !hasVersionedRowsForName) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_INVALID_MISSING_SENSOR_TAG_OR_FORMULA,
        explain: "'sensorTag' or 'formula' is missing",
        location: { file: file.name, line },
        data: {
          hint: sensorTag + " " + formula,
          value: name,
        },
      });
    }

    if (startsAt && formula.trim().length === 0) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_EMPTY_FORMULA,
        explain: "'formula' is missing for formula version",
        location: { file: file.name, line },
        data: {
          hint: startsAt,
          value: name,
        },
      });
    }

    if (hasInvalidQuantitySensorTagSize(sensorTag)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_FILE_SENSOR_TAG_TOO_LONG,
        explain: "'sensorTag' is too long",
        location: { file: file.name, line },
        data: {
          key: "sensorTag",
          hint: sensorTag,
          value: name,
        },
      });
    }

    if (hasInvalidFormulaSize(formula)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_FILE_FORMULA_TOO_LONG,
        explain: "'formula' is too long",
        location: { file: file.name, line },
        data: {
          key: "formula",
          hint: formula,
          value: name,
        },
      });
    }

    if (hasInvalidQuantityNameFormat(name)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_INVALID_QUANTITY_NAME_FORMAT,
        explain: "quantity 'name' not allowed",
        location: { file: file.name, line },
        data: {
          hint: "name",
          value: name,
        },
      });
    }

    if (hasInvalidDescriptionSize(description)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_FILE_DESCRIPTION_TOO_LONG,
        explain: "'description' is too long",
        location: { file: file.name, line },
        data: {
          key: "description",
          hint: description,
          value: name,
        },
      });
    }

    if (hasInvalidPairValueFormulaSensor(sensorTag, formula)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_INVALID_PAIR_VALUE_FORMULA_SENSOR,
        explain: "'formula' and 'sensorTag' cannot be defined at the same time",
        location: { file: file.name, line },
        data: {
          hint: formula,
          value: name,
        },
      });
    }

    if (hasMinSuperiorToMax(min, max)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_MIN_SUP_MAX_INVALID,
        explain: "minimum value should be inferior to maximum value",
        location: { file: file.name, line },
        data: {
          hint: min + "<" + max,
          value: name,
        },
      });
    }

    if (hasFiltersNotFloat(min, max, maxStd)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_MIN_MAX_STD_INVALID,
        explain: "'minValue', 'maxValue', 'maxStd' value should be a float",
        location: { file: file.name, line },
        data: {
          hint: min + " " + max + " " + maxStd,
          value: name,
        },
      });
    }

    if (hasNegativeMaxStd(maxStd)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_NEGATIVE_MAXSTD_INVALID,
        explain: "'maximum standard deviation' should be positive",
        location: { file: file.name, line },
        data: {
          hint: maxStd,
          value: name,
        },
      });
    }

    if (hasFormulaQuantitiesWithMaxStd(formula, maxStd)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_MAXSTD_WITH_FORMULA_INVALID,
        explain: "'formula' and 'maximum standard deviation' cannot be defined at the same time",
        location: { file: file.name, line },
        data: {
          hint: formula + " " + maxStd,
          value: name,
        },
      });
    }

    if (hasInvalidFormatEquipments(equipments)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_INVALID_EQUIPMENTS,
        explain: "'equipments' format is invalid",
        location: { file: file.name, line },
        data: {
          hint: equipments,
          value: name,
        },
      });
    }

    if (hasInvalidLockedField(locked)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_INVALID_LOCKED_FIELD,
        explain: "'locked' field must contain booleans (true or false) or be empty",
        location: { file: file.name, line },
        data: {
          hint: locked,
          value: name,
        },
      });
    }

    if (hasInvalidHiddenField(hidden)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_INVALID_HIDDEN_FIELD,
        explain: "'hidden' field must contain booleans (true or false) or be empty",
        location: { file: file.name, line },
        data: {
          hint: hidden,
          value: name,
        },
      });
    }

    if (hasLockedTrueWithoutFormula(locked, formula)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_LOCKED_TRUE_WITHOUT_FORMULA,
        explain: "'locked' field can't be true for a quantity linked to a sensor",
        location: { file: file.name, line },
        data: {
          key: "locked",
          hint: `'locked' = true sans formule`,
          value: name,
        },
      });
    }

    if (startsAt && hasInvalidStartDateFormat(startsAt)) {
      errors.push({
        kind: QUANTITY_PARSING_ERROR.DATA_START_DATE_INVALID_FORMAT,
        explain: "'startDate' must be in ISO format",
        location: { file: file.name, line },
        data: {
          hint: startsAt,
          value: name,
        },
      });
    }
  }

  for (const [name, group] of rowsByName.entries()) {
    const hasStartDate = group.some(row => row.startsAt.trim() !== "");
    const baseRows = group.filter(row => row.startsAt.trim() === "");
    const hasBaseRow = baseRows.length > 0;

    if (hasStartDate && !hasBaseRow) {
      for (const row of group.filter(candidate => candidate.startsAt.trim() !== "")) {
        errors.push({
          kind: QUANTITY_PARSING_ERROR.DATA_START_DATE_MISSING_BASE,
          explain: "startDate requires another row without startDate",
          location: { file: file.name, line: row.line },
          data: {
            hint: row.startsAt,
            value: name,
          },
        });
      }
    }

    if (hasStartDate) {
      if (baseRows.length > 1) {
        for (const row of baseRows) {
          errors.push({
            kind: QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_MULTIPLE_BASE_ROWS,
            explain: "versioned quantity cannot contain multiple base rows",
            location: { file: file.name, line: row.line },
            data: {
              hint: row.startsAt,
              value: name,
            },
          });
        }
      }

      const baseRow = baseRows[0] ?? null;
      if (baseRow && baseRow.formula.trim().length === 0) {
        errors.push({
          kind: QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_BASE_FORMULA_REQUIRED,
          explain: "versioned quantity requires a base row with a formula",
          location: { file: file.name, line: baseRow.line },
          data: {
            hint: baseRow.formula,
            value: name,
          },
        });
      }

      if (baseRow && baseRow.sensorTag.trim().length > 0) {
        errors.push({
          kind: QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_BASE_SENSOR_TAG_NOT_ALLOWED,
          explain: "versioned quantity base row cannot define a sensorTag",
          location: { file: file.name, line: baseRow.line },
          data: {
            hint: baseRow.sensorTag,
            value: name,
          },
        });
      }

      if (baseRow) {
        for (const row of group.filter(candidate => candidate.startsAt.trim() !== "")) {
          if (!hasSparseVersionMetadataMismatch(baseRow, row)) continue;
          errors.push({
            kind: QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_METADATA_MISMATCH,
            explain: "dated formula rows can only populate metadata matching the base row",
            location: { file: file.name, line: row.line },
            data: {
              hint: row.startsAt,
              value: name,
            },
          });
        }
      }
    }

    const startsAtSet = new Set<number>();
    const versionRows = group
      .filter(row => row.startsAt.trim() !== "")
      .sort(
        (a, b) =>
          (normalizeStartDateToUTC(a.startsAt)?.toMillis() ?? 0) -
          (normalizeStartDateToUTC(b.startsAt)?.toMillis() ?? 0),
      );

    for (const row of versionRows) {
      const startsAtValue = row.startsAt.trim();
      const normalizedStartDate = normalizeStartDateToUTC(startsAtValue);
      if (!normalizedStartDate) continue;
      const normalizedKey = normalizedStartDate.toMillis();

      if (startsAtSet.has(normalizedKey)) {
        errors.push({
          kind: QUANTITY_PARSING_ERROR.DATA_FORMULA_VERSION_DUPLICATE_STARTS_AT,
          explain: "duplicate startDate for formula version",
          location: { file: file.name, line: row.line },
          data: {
            hint: startsAtValue,
            value: name,
          },
        });
        continue;
      }
      startsAtSet.add(normalizedKey);
      formulaVersions.push({
        name,
        formula: row.formula,
        startsAt: normalizedStartDate.toJSDate(),
      });
    }

    const baseRow =
      group.find(row => !row.startsAt && (row.formula.trim() || row.sensorTag.trim())) ??
      group.find(row => !row.startsAt) ??
      group[0];

    if (!baseRow) continue;

    const equipmentNames =
      baseRow.equipments === null ? [] : baseRow.equipments.split(",").filter(f => f !== null && f);

    quantities.push({
      name,
      unit: baseRow.unit,
      description: baseRow.description,
      sensorTag: baseRow.sensorTag,
      formula: baseRow.formula,
      categories: {
        systemId: baseRow.systemId,
        equipmentTypeId: baseRow.equipmentTypeId,
        equipmentNames,
      },
      locked: baseRow.locked === "true" ? true : baseRow.locked === "false" ? false : null,
      hidden: baseRow.hidden === "true" ? true : baseRow.hidden === "false" ? false : null,
      filter: {
        min: baseRow.min ? parseFloat(baseRow.min) : null,
        max: baseRow.max ? parseFloat(baseRow.max) : null,
        maxStd: baseRow.maxStd ? parseFloat(baseRow.maxStd) : null,
      },
    });
  }

  return errors.length > 0
    ? Err(errors)
    : Ok({
        quantities,
        formulaVersions,
        ignoredQuantities,
        ignoredFormulas,
      });
};
